import React from 'react';
import { useTranslation } from 'next-i18next';
import useRouter from 'src/shared/hooks/useRouter';

import RoutePaths from 'src/config/RoutePaths';
import TitleText from 'src/shared/components/TitleText';
import RoundedButton from 'src/shared/components/RoundedButton';

import styles from './styles.module.scss';
import Card from '../Card';
import GaAction from 'src/config/GaAction';
import GaCategory from 'src/config/GaCategory';
import GaService from 'src/services/GaService';

interface Props {
    className?: string;
    isMasked?: boolean;
    themeColor?: string;
    description?: string | null;
}

interface DescriptionProps {
    description?: string | null;
}

interface KeyInfoProps {
    value: string;
    type: string;
}

const Description: React.FC<DescriptionProps> = ({ description }) => {
    const isMasked = !description;

    return (
        <div className={styles.description}>
            {isMasked && (
                <>
                    <div className={styles.maskedLine} />
                    <div className={styles.maskedLine} />
                    <div className={styles.maskedLineShort} />
                </>
            )}
            {!isMasked && description}
        </div>
    );
};
Description.displayName = 'Description';

Description.defaultProps = {
    description: null,
};

const KeyInfo: React.FC<KeyInfoProps> = ({ value, type }) => {
    return (
        <div className={styles.keyInfo}>
            <div className={styles.keyInfoValue}>{value}</div>
            <div className={styles.keyInfoType}>{type}</div>
        </div>
    );
};
KeyInfo.displayName = 'KeyInfo';

const LOCK_ICON_CLASS = 'icon-lock';

// TODO divide into components
const FundCard: React.FC<Props> = (props) => {
    const { t } = useTranslation();
    const router = useRouter();
    const { isMasked } = props;

    const redirectToRegister = () => {
        GaService.callEvent(GaCategory.marketing, GaAction.clickRegisterButton, `${router.route}: fund card`);
        router.pushPath(RoutePaths.REGISTER);
    };

    let title = '';
    let iconClassName = '';
    let name = '';
    let company = '';
    let fundIconStyles = {};
    let keyInfoOneType = '';
    let keyInfoOneValue = '';
    let keyInfoTwoType = '';
    let keyInfoTwoValue = '';
    let buttonText = '';

    if (isMasked) {
        title = t('article_fund_card_masked_title');
        iconClassName = LOCK_ICON_CLASS;
        name = t('article_fund_card_masked_fund_name');
        company = t('article_fund_card_masked_company');
        fundIconStyles = { backgroundColor: props.themeColor };
        keyInfoOneType = t('article_fund_card_masked_industry');
        keyInfoOneValue = t('article_fund_card_masked_industry_value');
        keyInfoTwoType = t('article_fund_card_masked_valuation');
        keyInfoTwoValue = t('article_fund_card_masked_valuation_value');
        buttonText = t('sign_up_to_unlock_details_button');
    }

    return (
        <Card className={`${styles.fundCardContainer} ${props.className}`}>
            <TitleText tag="h1" className={styles.title}>
                {title}
            </TitleText>
            <div className={styles.iconAndNameContainer}>
                <div className={styles.iconContainer} style={fundIconStyles}>
                    {!!iconClassName && <i className={`${styles.icon} ${iconClassName}`} />}
                </div>
                <div className={styles.nameAndCompany}>
                    <div className={styles.name}>{name}</div>
                    <div className={styles.company}>{company}</div>
                </div>
            </div>
            <Description description={props.description} />
            <div className={styles.keyInfos}>
                <KeyInfo type={keyInfoOneType} value={keyInfoOneValue} />
                <KeyInfo type={keyInfoTwoType} value={keyInfoTwoValue} />
            </div>
            <RoundedButton className={styles.button} type="primary-on-light" onClick={redirectToRegister}>
                {buttonText}
            </RoundedButton>
            <div className={styles.piOnlyReminder}>
                <i className={`${styles.piLockIcon} ${LOCK_ICON_CLASS}`} />
                <div className={styles.piOnlyText}>{t('professional_investor_only')}</div>
            </div>
        </Card>
    );
};

FundCard.displayName = 'FundCard';
export default FundCard;
